'use es6';

import keyMirror from 'react-utils/keyMirror';
import http from 'hub-http/clients/apiClient';
import I18n from 'I18n';
import Log from 'forms-utils-lib/logger/Logger';
import { isFormErrorHtml } from 'FormsUI/utils/ApiUtils';
import { getFormKey } from 'FormsUI/earlyRequest/earlyRequestsShared';
import getFromEarlyRequest from 'forms-utils-lib/earlyRequest/getFromEarlyRequest';
import { ER_FORM_ALIAS } from 'FormsCommonComponents/earlyRequest/earlyRequestKeys';
import { getCrmSearchForForms } from 'FormsUI/api/CrmSearch';
import { PROPERTIES } from 'FormsUI/components/dashboard/frameworkTable/constants';
const BASE_URL = '/forms/v2';
export const FormErrors = keyMirror({
  NO_NETWORK: null,
  DUPLICATE_NAME: null,
  TOO_MANY_FORMS: null,
  FORM_NAME_TOO_LONG: null,
  TOO_MANY_FIELDS: null,
  NOT_FOUND: null,
  CONCURRENT_UPDATE: null,
  CONCURRENT_UPDATE_BY_USER: null,
  CONCURRENT_UPDATE_BY_SYSTEM: null,
  FIELD_NAME_DOES_NOT_MATCH_PROPERTY_NAME: null,
  MISSING_FORM_FIELD_NAME: null,
  USER_DOES_NOT_HAVE_MARKETABLE_CONTACTS_WRITE_SCOPE: null,
  NOT_ENOUGH_PRIVILEGES: null,
  UNKNOWN: null,
  USER_NOT_VERIFIED: null,
  FOLLOW_UP_EMAIL_NOT_VALID: null,
  SUBJECT_LINE_NOT_SET: null,
  MISSING_NAME: null,
  FIELD_LABEL_CONTAINS_RESTRICTED_KEYWORDS: null,
  NO_FORM: null,
  DUPLICATE_FIELD: null,
  UNSAFE_RICHTEXT_HTML: null,
  UNSAFE_INLINE_MESSAGE_HTML: null,
  INVALID_REDIRECT_URL: null,
  INVALID_ENUMERATION_OPTIONS: null,
  FORM_FIELDS_NOT_VALID: null,
  INVALID_PAYMENT_SESSION_TEMPLATE_IDS: null,
  DUPLICATE_VALUES_IN_ENUM_FIELD: null
});
export const FormCreationErrors = keyMirror({
  DUPLICATE_NAME: null,
  TOO_MANY_FORMS: null,
  TOO_MANY_FIELDS: null,
  NO_NETWORK: null,
  UNKNOWN: null
});
export const FormGenerationErrors = keyMirror({
  INVALID_TEMPLATE: null,
  INBOX_ID_NON_SUPPORT_FORM: null,
  UNKNOWN: null
});

// Take the form definition as returned by the service and transform it into
// what we want to use.
function processFormDefinition(formDefinition) {
  return Object.assign({}, formDefinition, {
    updatedAt: I18n.moment.utc(formDefinition.updatedAt),
    createdAt: I18n.moment.utc(formDefinition.createdAt)
  });
}
function _fetchForm(guid) {
  return http.get(`${BASE_URL}/forms/${guid}`).then(processFormDefinition);
}
export const getFormsBatch = guid => http.getWithResponse(`${BASE_URL}/forms/batch`, {
  query: {
    guid
  }
}).then(({
  xhr,
  data
}) => {
  return {
    forms: data,
    xhr
  };
});
export function getForm(guid) {
  return getFromEarlyRequest(getFormKey(guid), () => _fetchForm(guid), {
    alias: ER_FORM_ALIAS,
    onEarlyRequestFinish: processFormDefinition
  });
}
export const getAllFormsForComparison = ({
  searchTerm
}) => {
  const query = {
    query: searchTerm
  };
  return getCrmSearchForForms(query).then(response => {
    const {
      total,
      offset,
      results
    } = response;
    const forms = results.map(crmObject => {
      const properties = crmObject.properties;
      return {
        name: properties[PROPERTIES.NAME].value,
        guid: properties[PROPERTIES.FORM_ID].value
      };
    });
    return {
      forms,
      total,
      offset
    };
  });
};
export function parseFormError(xhr) {
  try {
    if (isFormErrorHtml(xhr.responseText)) return FormErrors.UNKNOWN;
    const type = JSON.parse(xhr.responseText).type;
    return FormErrors[type] || FormErrors.UNKNOWN;
  } catch (error) {
    Log.warn(`Could not parse error type from XHR response: ${error}`, {
      error
    });
    return FormErrors.UNKNOWN;
  }
}
export function createForm(formDefinition) {
  return http.post(`${BASE_URL}/forms`, {
    data: formDefinition
  });
}
export function putFormDefinition(formGuid, formDefinition) {
  return http.post(`${BASE_URL}/forms/${formGuid}`, {
    data: formDefinition
  });
}
export function patchForm(guid, formDefinition) {
  return http.patch(`${BASE_URL}/forms/${guid}`, {
    data: formDefinition
  });
}
export function renameForm(guid, {
  name
}) {
  return http.put(`${BASE_URL}/forms/rename/${guid}`, {
    data: {
      name
    }
  });
}
export function deleteForm(guid) {
  return http.delete(`${BASE_URL}/forms/${guid}`);
}
export function cloneForm({
  guid,
  name,
  businessUnitId
}) {
  return http.post(`${BASE_URL}/forms/${guid}/clone`, {
    data: {
      name,
      businessUnitId
    }
  });
}
export function deleteForms(guids) {
  return http.delete(`${BASE_URL}/forms/batch`, {
    query: {
      guid: guids
    }
  });
}